import { FC, ChangeEvent, useState, useEffect } from "react";
import dayjs from "dayjs";
import Typography from "@/components/Typography";
import TextField from "@/components/TextField";
import Divider from "@/components/Divider";
import Switch from "@/components/Switch";
import Checkbox from "@/components/Checkbox";
import { FormControlLabel } from "@/components/FormController";
import { IconButton } from "@/components/Button";
import { CloseIcon } from "@/components/Icon";
import { Skeleton } from "@/components/Loader";
import { useResponsive, useLock, useFeatureFlags, useTripType } from "@/hooks";
import Button from "@/components/Button";
import { MobileModal } from "@/components/Modal";
import { MobilePaperTitle } from "@/components/PageTitle";
import styles from "./FilterSection.module.scss";
import cx from "classnames";

export type FiltersType = {
  locations: Array<string>;
  industries: Array<string>;
  multipleCandidates: boolean;
  dateFrom: number;
  dateTo: number;
  roles: Array<string>;
  startMonths: Array<string>;
  categories: Array<string>;
};

type Props = {
  filterObj: FiltersType;
  onChangeFilter: (newFilterObj: FiltersType) => void;
  regions: Array<string>;
  industries: Array<string>;
  roles: Array<string>;
  onCloseFilters: () => void;
  onSearch: (searchKey: string) => void;
  searchKey: string;
  isLoading?: boolean;
  totalCount: number;
};

enum DateFiltersValues {
  PAST_24_HOURS = "Past 24 hours",
  PAST_WEEK = "Past Week",
  PAST_MONTH = "Past Month",
}

const FilterSection: FC<Props> = ({
  filterObj,
  onChangeFilter,
  regions,
  roles,
  industries,
  onCloseFilters,
  onSearch,
  searchKey,
  isLoading,
  totalCount,
}) => {
  const [showAllLocations, setShowAllLocations] = useState<boolean>(false);
  const [showAllIndustries, setShowAllIndustries] = useState<boolean>(false);
  const [showAllRoles, setShowAllRoles] = useState<boolean>(false);
  const [showAllMonths, setShowAllMonths] = useState<boolean>(false);
  const [mobileFilterObj, setMobileFilterObj] =
    useState<FiltersType>(filterObj);

  const { isMobile } = useResponsive();
  const { isSuperliteTrip, isWHArrivalOnly } = useTripType();
  const {
    flags: { replaceWHJobsWithSLJobs },
  } = useFeatureFlags();
  const DateFilters = [
    DateFiltersValues.PAST_24_HOURS,
    DateFiltersValues.PAST_WEEK,
    DateFiltersValues.PAST_MONTH,
  ];

  const months = dayjs.months();

  const onChangeDate = (
    event: ChangeEvent<HTMLInputElement>,
    dateLabel: DateFiltersValues
  ) => {
    if (!event.target.checked) {
      if (isMobile) {
        setMobileFilterObj({ ...mobileFilterObj, dateFrom: 0, dateTo: 0 });
      } else {
        onChangeFilter({ ...filterObj, dateFrom: 0, dateTo: 0 });
      }
      return;
    }
    const currentDate = dayjs().unix();
    if (dateLabel === DateFiltersValues.PAST_24_HOURS) {
      const pastDay = dayjs().subtract(1, "d").unix();
      if (isMobile) {
        setMobileFilterObj({
          ...mobileFilterObj,
          dateFrom: pastDay,
          dateTo: currentDate,
        });
      } else {
        onChangeFilter({
          ...filterObj,
          dateFrom: pastDay,
          dateTo: currentDate,
        });
      }
    }
    if (dateLabel === DateFiltersValues.PAST_WEEK) {
      const pastWeek = dayjs().subtract(1, "w").unix();
      if (isMobile) {
        setMobileFilterObj({
          ...mobileFilterObj,
          dateFrom: pastWeek,
          dateTo: currentDate,
        });
      } else {
        onChangeFilter({
          ...filterObj,
          dateFrom: pastWeek,
          dateTo: currentDate,
        });
      }
    }
    if (dateLabel === DateFiltersValues.PAST_MONTH) {
      const pastMonth = dayjs().subtract(1, "M").unix();
      if (isMobile) {
        setMobileFilterObj({
          ...mobileFilterObj,
          dateFrom: pastMonth,
          dateTo: currentDate,
        });
      } else {
        onChangeFilter({
          ...filterObj,
          dateFrom: pastMonth,
          dateTo: currentDate,
        });
      }
    }
  };

  const isDateSelected = (dateLabel: string) => {
    const currentDateRange = [];
    if (isMobile) {
      if (mobileFilterObj.dateFrom) {
        currentDateRange.push(mobileFilterObj.dateFrom);
      }
      if (mobileFilterObj.dateTo) {
        currentDateRange.push(mobileFilterObj.dateTo);
      }
    } else {
      if (filterObj.dateFrom) {
        currentDateRange.push(filterObj.dateFrom);
      }
      if (filterObj.dateTo) {
        currentDateRange.push(filterObj.dateTo);
      }
    }

    if (currentDateRange.length === 0) return false;
    const firstDate = currentDateRange?.[0] || 0;

    const pastDay = dayjs().subtract(1, "d").unix();
    const pastWeek = dayjs().subtract(1, "w").unix();
    const pastMonth = dayjs().subtract(1, "M").unix();

    if (dateLabel === DateFiltersValues.PAST_24_HOURS) {
      return firstDate - pastDay <= 0 && firstDate - pastWeek > 0;
    }

    if (dateLabel === DateFiltersValues.PAST_WEEK) {
      return firstDate - pastWeek <= 0 && firstDate - pastMonth > 0;
    }
    if (dateLabel === DateFiltersValues.PAST_MONTH) {
      return firstDate - pastMonth <= 0;
    }
  };

  const onApplyFilters = () => {
    onChangeFilter(mobileFilterObj);
    onCloseFilters();
  };

  const onClearFilters = () => {
    onChangeFilter({
      dateFrom: 0,
      dateTo: 0,
      roles: [],
      locations: [],
      industries: [],
      startMonths: [],
      multipleCandidates: false,
      categories: filterObj.categories,
    });
    setMobileFilterObj({
      dateFrom: 0,
      dateTo: 0,
      roles: [],
      locations: [],
      industries: [],
      startMonths: [],
      multipleCandidates: false,
      categories: filterObj.categories,
    });
    onSearch("");
    onCloseFilters();
  };

  const handleCloseFilters = () => {
    onChangeFilter(filterObj);
    onSearch(searchKey);
    setMobileFilterObj(filterObj);
    onCloseFilters();
  };

  const filterComponents = (
    <>
      <Typography
        color={isMobile ? "text.primary" : "text.secondary"}
        variant={isMobile ? "h6" : "subtitle1"}
        className={styles.filterTitle}
      >
        City
      </Typography>
      {isLoading ? (
        <Skeleton variant="text" />
      ) : (
        <div className={styles.optionsContainer}>
          {(showAllLocations ? regions : regions.slice(0, 4))?.map(
            (eachRegion) => (
              <FormControlLabel
                key={eachRegion}
                control={
                  <Checkbox
                    classes={{ root: styles.checkbox }}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      const currentLocations = isMobile
                        ? mobileFilterObj.locations
                        : filterObj.locations;
                      // const currentLocations = filterObj.locations;
                      const isInFilter = Boolean(
                        currentLocations.find((each) => each === eachRegion)
                      );
                      if (isInFilter) {
                        if (isMobile) {
                          setMobileFilterObj({
                            ...mobileFilterObj,
                            locations: currentLocations.filter(
                              (each) => each !== eachRegion
                            ),
                          });
                        } else {
                          onChangeFilter({
                            ...filterObj,
                            locations: currentLocations.filter(
                              (each) => each !== eachRegion
                            ),
                          });
                        }
                      } else {
                        if (isMobile) {
                          setMobileFilterObj({
                            ...mobileFilterObj,
                            locations: currentLocations.concat(eachRegion),
                          });
                        } else {
                          onChangeFilter({
                            ...filterObj,
                            locations: currentLocations.concat(eachRegion),
                          });
                        }
                      }
                    }}
                    checked={
                      isMobile
                        ? Boolean(
                            mobileFilterObj.locations.find(
                              (each) => each === eachRegion
                            )
                          )
                        : Boolean(
                            filterObj.locations.find(
                              (each) => each === eachRegion
                            )
                          )
                    }
                  />
                }
                label={eachRegion}
                className={styles.filterItem}
                classes={{
                  root: styles.formLabelRoot,
                  label: styles.checkboxLabel,
                }}
              />
            )
          )}
          {regions?.length > 4 && (
            <p
              role="button"
              className={styles.showAllButton}
              onClick={() => setShowAllLocations(!showAllLocations)}
            >
              {showAllLocations ? "Show less" : "Show All"}
            </p>
          )}
        </div>
      )}
      <Divider className={styles.divider} />
      <Typography
        color={isMobile ? "text.primary" : "text.secondary"}
        variant={isMobile ? "h6" : "subtitle1"}
        className={styles.filterTitle}
      >
        Industry
      </Typography>
      {isLoading ? (
        <Skeleton variant="text" />
      ) : (
        <div className={styles.optionsContainer}>
          {(showAllIndustries ? industries : industries.slice(0, 4))?.map(
            (eachIndustry) => (
              <FormControlLabel
                key={eachIndustry}
                control={
                  <Checkbox
                    classes={{ root: styles.checkbox }}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      const currentIndustries = isMobile
                        ? mobileFilterObj.industries
                        : filterObj.industries;
                      // const currentIndustries = filterObj.industries;
                      const isInFilter = Boolean(
                        currentIndustries.find((each) => each === eachIndustry)
                      );
                      if (isInFilter) {
                        if (isMobile) {
                          setMobileFilterObj({
                            ...mobileFilterObj,
                            industries: currentIndustries.filter(
                              (each) => each !== eachIndustry
                            ),
                          });
                        } else {
                          onChangeFilter({
                            ...filterObj,
                            industries: currentIndustries.filter(
                              (each) => each !== eachIndustry
                            ),
                          });
                        }
                      } else {
                        if (isMobile) {
                          setMobileFilterObj({
                            ...mobileFilterObj,
                            industries: currentIndustries.concat(eachIndustry),
                          });
                        } else {
                          onChangeFilter({
                            ...filterObj,
                            industries: currentIndustries.concat(eachIndustry),
                          });
                        }
                      }
                    }}
                    checked={
                      isMobile
                        ? Boolean(
                            mobileFilterObj.industries.find(
                              (each) => each === eachIndustry
                            )
                          )
                        : Boolean(
                            filterObj.industries.find(
                              (each) => each === eachIndustry
                            )
                          )
                    }
                  />
                }
                label={eachIndustry}
                className={styles.filterItem}
                classes={{
                  root: styles.formLabelRoot,
                  label: styles.checkboxLabel,
                }}
              />
            )
          )}
          {industries?.length > 4 && (
            <p
              role="button"
              className={styles.showAllButton}
              onClick={() => setShowAllIndustries(!showAllIndustries)}
            >
              {showAllIndustries ? "Show less" : "Show All"}
            </p>
          )}
        </div>
      )}
      {!isSuperliteTrip && !isWHArrivalOnly && !replaceWHJobsWithSLJobs && (
        <>
          <Divider className={styles.divider} />
          <Typography
            color={isMobile ? "text.primary" : "text.secondary"}
            variant={isMobile ? "h6" : "subtitle1"}
            className={styles.filterTitle}
          >
            Role
          </Typography>
          {isLoading ? (
            <Skeleton variant="text" />
          ) : (
            <div className={styles.optionsContainer}>
              {(showAllRoles ? roles : roles?.slice(0, 4)).map((eachRole) => (
                <FormControlLabel
                  key={eachRole}
                  control={
                    <Checkbox
                      classes={{ root: styles.checkbox }}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        const currentRoles = isMobile
                          ? mobileFilterObj.roles
                          : filterObj.roles;
                        // const currentRoles = filterObj.roles;
                        const isInFilter = Boolean(
                          currentRoles.find((each) => each === eachRole)
                        );
                        if (isInFilter) {
                          if (isMobile) {
                            setMobileFilterObj({
                              ...mobileFilterObj,
                              roles: currentRoles.filter(
                                (each) => each !== eachRole
                              ),
                            });
                          } else {
                            onChangeFilter({
                              ...filterObj,
                              roles: currentRoles.filter(
                                (each) => each !== eachRole
                              ),
                            });
                          }
                        } else {
                          if (isMobile) {
                            setMobileFilterObj({
                              ...mobileFilterObj,
                              roles: currentRoles.concat(eachRole),
                            });
                          } else {
                            onChangeFilter({
                              ...filterObj,
                              roles: currentRoles.concat(eachRole),
                            });
                          }
                        }
                      }}
                      checked={
                        isMobile
                          ? Boolean(
                              mobileFilterObj.roles.find(
                                (each) => each === eachRole
                              )
                            )
                          : Boolean(
                              filterObj.roles.find((each) => each === eachRole)
                            )
                      }
                    />
                  }
                  label={eachRole}
                  className={styles.filterItem}
                  classes={{
                    root: styles.formLabelRoot,
                    label: styles.checkboxLabel,
                  }}
                />
              ))}
              {roles.length > 4 && (
                <p
                  role="button"
                  className={styles.showAllButton}
                  onClick={() => setShowAllRoles(!showAllRoles)}
                >
                  {showAllRoles ? "Show less" : "Show All"}
                </p>
              )}
            </div>
          )}
        </>
      )}

      {!isSuperliteTrip && !isWHArrivalOnly && !replaceWHJobsWithSLJobs && (
        <>
          <Divider className={styles.divider} />
          <Typography
            color="text.secondary"
            variant="subtitle1"
            className={styles.filterTitle}
          >
            Start Month
          </Typography>

          {(showAllMonths ? months : months?.slice(0, 4)).map((eachMonth) => (
            <FormControlLabel
              key={eachMonth}
              control={
                <Checkbox
                  classes={{ root: styles.checkbox }}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    const currentMonths = isMobile
                      ? mobileFilterObj.startMonths
                      : filterObj.startMonths;
                    // const currentMonths = filterObj.startMonths;
                    const isInFilter = Boolean(
                      currentMonths.find((each) => each === eachMonth)
                    );
                    if (isInFilter) {
                      if (isMobile) {
                        setMobileFilterObj({
                          ...mobileFilterObj,
                          startMonths: currentMonths.filter(
                            (each) => each !== eachMonth
                          ),
                        });
                      } else {
                        onChangeFilter({
                          ...filterObj,
                          startMonths: currentMonths.filter(
                            (each) => each !== eachMonth
                          ),
                        });
                      }
                    } else {
                      if (isMobile) {
                        setMobileFilterObj({
                          ...mobileFilterObj,
                          startMonths: currentMonths.concat(eachMonth),
                        });
                      } else {
                        onChangeFilter({
                          ...filterObj,
                          startMonths: currentMonths.concat(eachMonth),
                        });
                      }
                    }
                  }}
                  checked={
                    isMobile
                      ? Boolean(
                          mobileFilterObj.startMonths.find(
                            (each) => each === eachMonth
                          )
                        )
                      : Boolean(
                          filterObj.startMonths.find(
                            (each) => each === eachMonth
                          )
                        )
                  }
                />
              }
              label={eachMonth}
              className={styles.filterItem}
              classes={{
                root: styles.formLabelRoot,
                label: styles.checkboxLabel,
              }}
            />
          ))}

          <p
            role="button"
            className={styles.showAllButton}
            onClick={() => setShowAllMonths(!showAllMonths)}
          >
            {showAllMonths ? "Show less" : "Show All"}
          </p>
        </>
      )}
      {!isSuperliteTrip && !isWHArrivalOnly && !replaceWHJobsWithSLJobs && (
        <>
          <Divider className={styles.divider} />
          <Typography
            color="text.secondary"
            variant="subtitle1"
            className={styles.filterTitle}
          >
            Date Posted
          </Typography>
          {DateFilters?.map((eachDate) => (
            <FormControlLabel
              key={eachDate}
              control={
                <Checkbox
                  classes={{ root: styles.checkbox }}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    onChangeDate(event, eachDate)
                  }
                  checked={isDateSelected(eachDate)}
                  disabled={isLoading}
                />
              }
              label={eachDate}
              className={styles.filterItem}
              classes={{
                root: styles.formLabelRoot,
                label: styles.checkboxLabel,
              }}
            />
          ))}
        </>
      )}
      {!isSuperliteTrip && !isWHArrivalOnly && !replaceWHJobsWithSLJobs && (
        <>
          <Divider className={styles.divider} />
          <FormControlLabel
            className={styles.switchFormLabel}
            control={
              <Switch
                checked={
                  isMobile
                    ? mobileFilterObj.multipleCandidates
                    : filterObj.multipleCandidates
                }
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  if (isMobile) {
                    setMobileFilterObj({
                      ...mobileFilterObj,
                      multipleCandidates: event.target.checked,
                    });
                  } else {
                    onChangeFilter({
                      ...filterObj,
                      multipleCandidates: event.target.checked,
                    });
                  }
                }}
                disabled={isLoading}
              />
            }
            label="Multiple Positions"
            classes={{ root: styles.switchFormRoot, label: styles.switchLabel }}
          />
        </>
      )}
    </>
  );

  const countLabel = isLoading
    ? ""
    : totalCount < 10
    ? totalCount
    : totalCount < 50
    ? "+10"
    : totalCount < 100
    ? "+50"
    : "+100";

  return (
    <>
      {isMobile ? (
        <MobileModal>
          <MobilePaperTitle
            onGoBack={handleCloseFilters}
            iconType="close"
            title="Filters"
          >
            <Button
              variant="text"
              className={styles.resetButton}
              onClick={onClearFilters}
            >
              Reset
            </Button>
          </MobilePaperTitle>
          <div className={styles.mobileContent}>{filterComponents}</div>

          <div className={styles.actionContainer}>
            <Button
              onClick={onApplyFilters}
              className={styles.applyButton}
              disabled={isLoading}
            >
              {isLoading ? "Loading...." : `Show results`}
            </Button>
          </div>
        </MobileModal>
      ) : (
        <div className={styles.container}>
          <div className={styles.titleContainer}>
            <Typography
              variant="subtitle1"
              color={isMobile ? "text.primary" : "text.secondary"}
              className={styles.title}
            >
              Filters
            </Typography>
            <IconButton
              onClick={handleCloseFilters}
              className={cx(styles.closeButton, styles.closeButtonMotion)}
            >
              <CloseIcon />
            </IconButton>
          </div>
          <TextField
            value={searchKey}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              onSearch(event.target.value)
            }
            placeholder="Search by keyword..."
          />
          {filterComponents}
        </div>
      )}
    </>
  );
};

export default FilterSection;
